/**
 * Consent-Zustand der App.
 *
 * Das Cookie `cookie_consent` gehoert der Marketing-Seite feedoptimizer.ai und
 * wird ueber `domain=.feedoptimizer.ai` mit der App geteilt. ERTEILT wird die
 * Einwilligung dort, im Web-Banner; die App erbt sie ueber das Cookie und
 * trackt danach voll.
 *
 * Genau deshalb schreibt und raeumt diese Datei das Cookie auch, und zwar in
 * derselben Form wie der Web-Banner: der WIDERRUF muss in der App moeglich
 * sein. Ihr einziger Schreib-Aufrufer ist die Karte "Cookie-Einstellungen" in
 * den Einstellungen. Ohne sie waere erteilt mit einem Klick und
 * zurueckgenommen gar nicht.
 *
 * Laenderweiche (Spec 2026-10-21, Abschnitt 3.8): das Cookie `fo_region=US`
 * setzt die Middleware der Marketing-Seite fuer US-Besucher. Die App LIEST es
 * nur, sie setzt es nie und hat keine eigene Geo-Quelle (E4). Ein Direktaufruf
 * der App ohne vorherigen Web-Besuch findet also kein Cookie und bleibt im
 * Opt-in, der sicheren Richtung. Eine eigene Middleware bekommt die App
 * bewusst nicht (E12): ein von Hand gesetztes `fo_region=US` wird hier
 * akzeptiert, der Nutzer schreibt sich damit nur selbst ins eigene Tracking.
 */

import { isE2eTestSession } from './e2eFlag';

export const CONSENT_COOKIE_NAME = 'cookie_consent';
/** Gesetzt von der Web-Middleware, hier nur gelesen (Spec E2, E4). */
export const REGION_COOKIE_NAME = 'fo_region';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
const COOKIE_DOMAIN = '.feedoptimizer.ai';

/**
 * Die Datenschutzerklaerung liegt auf der Marketing-Domain, nicht in dieser
 * App: sie hat weder Footer noch eigene Datenschutz-Route, und der Text, der
 * die hier verwalteten Cookies beschreibt, steht dort.
 *
 * Die Form der beiden URLs ist NACHGESEHEN, nicht geraten
 * (`feedoptimizer-web/src/i18n/routing.ts`): `'/privacy': '/privacy'` ist in
 * beiden Sprachen derselbe Pfad, `localePrefix: 'as-needed'` mit
 * `defaultLocale: 'en'` -- Deutsch traegt also das Praefix, Englisch nicht.
 * Wer eine der beiden Annahmen aendert, aendert sie im Web-Repo und muss hier
 * nachziehen; ein Import ueber die Repo-Grenze gibt es nicht.
 */
export function privacyPolicyUrl(language: 'de' | 'en'): string {
  return language === 'de'
    ? 'https://www.feedoptimizer.ai/de/privacy'
    : 'https://www.feedoptimizer.ai/privacy';
}

export interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

/**
 * `optin`: alles wie vor der Laenderweiche, Einwilligung nur per Cookie.
 * `us_optout`: US-Besucher ohne GPC, Vorgabe granted bis zum Widerspruch.
 * `us_gpc`: US-Besucher mit Global Privacy Control, alles abgelehnt.
 */
export type ConsentRegime = 'optin' | 'us_optout' | 'us_gpc';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

/**
 * Die eine Stelle, an der dieses Modul ein Cookie im Jar sucht. Der Name steckt
 * nur in `CONSENT_COOKIE_NAME`; ein zweites Literal hier waere eine zweite
 * Quelle fuer dieselbe Wahrheit.
 */
function matchConsentCookie(): RegExpMatchArray | null {
  return readCookieJar().match(new RegExp(`(^| )${CONSENT_COOKIE_NAME}=([^;]+)`));
}

/**
 * Liest `document.cookie` fail-closed: wirft der Zugriff (gesperrtes Jar,
 * Sandbox-iframe, opaker Ursprung), gilt das Jar als leer. Leer heisst keine
 * gespeicherte Entscheidung und keine Region, also Opt-in-Regime ohne Grant.
 */
function readCookieJar(): string {
  try {
    return document.cookie;
  } catch {
    return '';
  }
}

/** Wie `matchConsentCookie`; der Name steckt nur in `REGION_COOKIE_NAME`. */
function matchRegionCookie(): RegExpMatchArray | null {
  return readCookieJar().match(new RegExp(`(^| )${REGION_COOKIE_NAME}=([^;]+)`));
}

/** Roher Wert von `fo_region` (nicht dekodiert, nicht getrimmt) oder null. */
export function readRegionCookie(): string | null {
  return matchRegionCookie()?.[2] ?? null;
}

/**
 * Global Privacy Control. Nur ein Boolean `true` zaehlt; der Typ fehlt in der
 * DOM-Bibliothek von TypeScript, daher die Erweiterung am Aufruf.
 */
export function hasGpcSignal(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: unknown }).globalPrivacyControl === true;
}

/**
 * Allowlist mit genau einem Wert: `'US'`, exakt verglichen (Spec E1). Jeder
 * andere Wert, leer, fehlend oder kaputt ist Opt-in. GPC wird NUR im
 * US-Regime gelesen; im Opt-in-Pfad aendert es nichts (E5).
 */
export function resolveConsentRegime(): ConsentRegime {
  if (readRegionCookie() !== 'US') return 'optin';
  return hasGpcSignal() ? 'us_gpc' : 'us_optout';
}

/**
 * Die Einwilligung, die gerade WIRKT, nicht nur die gespeicherte. Reihenfolge
 * nach Spec 3.8: GPC schlaegt im US-Regime alles, auch eine frueher
 * gespeicherte Zustimmung (E5, CCPA-Regs 7025(c)(2)). Danach gilt die
 * gespeicherte Entscheidung, in jedem Regime. Erst ohne sie greift die
 * US-Vorgabe granted. Im Opt-in-Regime ohne Cookie: null, nichts erteilt.
 */
export function effectiveConsent(): ConsentState | null {
  const regime = resolveConsentRegime();
  if (regime === 'us_gpc') return { necessary: true, analytics: false, marketing: false };
  const stored = parseConsentCookie();
  if (stored) return stored;
  if (regime === 'us_optout') return { necessary: true, analytics: true, marketing: true };
  return null;
}

export function parseConsentCookie(): ConsentState | null {
  const match = matchConsentCookie();
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[2])) as ConsentState;
    // Ein Cookie ohne die beiden Booleans ist keine Entscheidung, sondern ein
    // kaputter Wert. Er darf nicht als `undefined` in den dataLayer laufen, denn
    // GTM liest undefined als leeren String.
    if (typeof parsed?.analytics !== 'boolean' || typeof parsed?.marketing !== 'boolean') return null;
    return { necessary: true, analytics: parsed.analytics, marketing: parsed.marketing };
  } catch {
    return null;
  }
}

/**
 * Entspricht dem Signal `ad_user_data`, das `initConsent`/`applyConsentToGoogle`
 * aus demselben Feld ableiten: `marketing` der WIRKSAMEN Einwilligung. Genau
 * ein Boolean `true` oeffnet den user_data-Push fuer Enhanced Conversions.
 * Im Opt-in-Regime heisst das wie bisher: fehlendes oder kaputtes Cookie ist
 * `false` (sichere Richtung). Im US-Regime ohne Cookie ist es `true`, mit GPC
 * immer `false` (Spec 3.8, E5).
 */
export function hasAdUserDataConsent(): boolean {
  return effectiveConsent()?.marketing === true;
}

/**
 * Dieselbe Schreibform wie der Web-Banner (Domain-Cookie). Auf localhost und in
 * Vercel-Previews ist der Host kein Teilbereich von feedoptimizer.ai, der
 * Browser verwirft die Schreibung dann STILL. Deshalb die Gegenprobe, und sie
 * fragt nach dem WERT und nicht nach der blossen Anwesenheit eines Cookies
 * dieses Namens: nach der ersten Entscheidung liegt dort bereits ein
 * host-only-Cookie, und das erfuellte eine Anwesenheitsfrage auch dann, wenn
 * die Domain-Schreibung gerade wieder verworfen wurde. Jede WEITERE
 * Entscheidung waere dort still verlorengegangen -- und die weitere
 * Entscheidung ist in dieser App der WIDERRUF.
 *
 * Verglichen wird der kodierte Wert gegen `match[2]`, die zweite Gruppe des
 * Musters, denn der Jar gibt zurueck, was geschrieben wurde. In Produktion
 * stimmt er nach der Domain-Schreibung ueberein und die zweite Schreibung
 * unterbleibt, sonst laegen zwei Cookies gleichen Namens im Jar und welches
 * gewinnt, entschiede der Browser.
 *
 * RUECKGABE: ob am Ende wirklich dieser Wert im Jar steht. Die Gegenprobe lief
 * hier ohnehin, sie wurde nur nicht berichtet -- und der Aufrufer meldete
 * "gespeichert", ohne es zu wissen. Bei einer Erteilung waere das aergerlich,
 * bei einem Widerruf steht die Bestaetigung ueber weiterlaufendem Tracking.
 * `false` heisst: der Browser nimmt unser Cookie nicht (gesperrte Speicherung,
 * privater Modus mit harter Sperre).
 */
export function writeConsentCookie(state: ConsentState): boolean {
  const value = encodeURIComponent(JSON.stringify(state));
  document.cookie = `${CONSENT_COOKIE_NAME}=${value};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax;domain=${COOKIE_DOMAIN}`;
  let match = matchConsentCookie();
  if (!match || match[2] !== value) {
    document.cookie = `${CONSENT_COOKIE_NAME}=${value};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`;
    match = matchConsentCookie();
  }
  return !!match && match[2] === value;
}

/**
 * Google Consent Mode. Der Aufruf geht bewusst ueber das `arguments`-Objekt und
 * nicht ueber das Rest-Array: gtag.js liest genau diese Form aus dem dataLayer.
 */
function gtagUpdate(state: ConsentState): void {
  window.dataLayer = window.dataLayer || [];
  function gtag(..._args: unknown[]) {
    window.dataLayer.push(arguments as unknown as Record<string, unknown>);
  }
  gtag('consent', 'update', {
    analytics_storage: state.analytics ? 'granted' : 'denied',
    ad_storage: state.marketing ? 'granted' : 'denied',
    ad_user_data: state.marketing ? 'granted' : 'denied',
    ad_personalization: state.marketing ? 'granted' : 'denied',
  });
}

/**
 * Die drei Felder, mit denen sich eine App-Entscheidung im GTM von einer
 * Web-Entscheidung unterscheiden laesst. Der Web-Banner schickt DIESELBEN
 * sechs Feldnamen mit seinen eigenen Werten, damit EIN Trigger beide Flaechen
 * bedient -- die Namen sind die Naht und wandern nicht.
 *
 * Sie stehen als Konstanten hier und nicht als Parameter am Aufruf: in dieser
 * App gibt es genau eine Flaeche, die eine Consent-Entscheidung entgegennimmt,
 * die Karte in den Einstellungen. Ein Parameter behauptete eine Wahl, die es
 * nicht gibt.
 */
const CONSENT_SURFACE = 'app';
/** Kein A/B-Variantenname -- auf dieser Flaeche gibt es keine Varianten. */
const CONSENT_VARIANT = 'settings';
/** Was die Entscheidung ausgeloest hat: eine Aenderung in den Einstellungen. */
const CONSENT_TRIGGER = 'settings_change';

/**
 * Entscheidung aus den Einstellungen: dieselben sechs dataLayer-Felder wie im
 * Web, dazu der Google-Consent-Mode-Aufruf.
 *
 * Kein Feld wird weggelassen, auch wenn sein Wert hier konstant ist. Der
 * dataLayer ist KUMULATIV: ein ausgelassenes Feld loescht keinen frueheren
 * Wert, es laesst ihn lesbar stehen -- ein Tag saehe dann die Variante des
 * Web-Banners an einer App-Entscheidung.
 */
export function applyConsentToGoogle(state: ConsentState, language: 'de' | 'en'): void {
  if (isE2eTestSession()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'consent_update',
    consent_analytics: state.analytics,
    consent_marketing: state.marketing,
    consent_variant: CONSENT_VARIANT,
    consent_surface: CONSENT_SURFACE,
    consent_trigger: CONSENT_TRIGGER,
    consent_language: language,
  });
  gtagUpdate(state);
}

/**
 * Beim Start: liegt eine wirksame Einwilligung vor, wird sie an Google
 * gemeldet. Ohne sie passiert nichts, dann bleiben die Vorgaben aus index.html
 * stehen (im Opt-in-Regime alles abgelehnt). Kein `consent_update` von hier,
 * das Ereignis gehoert der Entscheidung im Banner.
 *
 * Im US-Regime traegt `consent_loaded` zusaetzlich `consent_regime`, damit GTM
 * das US-Regime vom Opt-in-Regime trennen kann. Eine US-Vorgabe (oder GPC)
 * von einer gespeicherten Entscheidung trennt das Feld NICHT: ein US-Nutzer
 * mit gespeichertem Cookie bekommt ebenfalls `us_optout`. Braucht GTM diese
 * Unterscheidung einmal, waere ein Feld `consent_source` der Folgevorgang.
 * Im Opt-in-Regime bleibt das Ereignis feldgleich wie vorher.
 */
export function initConsent() {
  const consent = effectiveConsent();
  if (!consent) return;
  if (isE2eTestSession()) return;
  const regime = resolveConsentRegime();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': undefined, // avoid duplicate
    event: 'consent_loaded',
    consent_analytics: consent.analytics,
    consent_marketing: consent.marketing,
    ...(regime !== 'optin' ? { consent_regime: regime } : {}),
  });
  gtagUpdate(consent);
}
